import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import withAuth from '../../auth/WithAuth';
import './users.scss';
import FurnitureNav from '../../../components/layout/Nav';
import { serverUrlAuth } from '../../../serverUrl';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    total_users: 0,
    artisan_count: 0,
    client_count: 0,
    bronze_count: 0,
    silver_count: 0,
    gold_count: 0,
   
  });

  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    tier: 'all',
    search: '',
  });
  const [showAddArtisanModal, setShowAddArtisanModal] = useState(false);
  const [showEditTierModal, setShowEditTierModal] = useState(false);
  const [selectedArtisan, setSelectedArtisan] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    tier: 'bronze', // Add tier to form data
  });
  const [tierFormData, setTierFormData] = useState({
    tier: 'bronze',
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchUserManagementData = async () => {
    try {
      const response = await serverUrlAuth.get('users/user-management-data/');

      setUsers(response.data.users);
      setStats({
        total_users: response.data.total_users,
        artisan_count: response.data.artisan_count,
        client_count: response.data.client_count,
        bronze_count: response.data.bronze_count || 0,
        silver_count: response.data.silver_count || 0,
        gold_count: response.data.gold_count || 0,
        
      });
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch user data');
    }
  };

  useEffect(() => {
    fetchUserManagementData();
    setLoading(false);
  }, []);

  const filteredUsers = users.filter((user) => {
    if (filters.role !== 'all' && user.role !== filters.role) return false;
    if (filters.status !== 'all' && user.status !== filters.status)
      return false;

    if (filters.tier !== 'all' && user.role === 'artisan') {
      if (user.tier !== filters.tier) return false;
    }

    if (
      filters.search &&
      !user.name?.toLowerCase().includes(filters.search.toLowerCase()) &&
      !user.email?.toLowerCase().includes(filters.search.toLowerCase())
    )
      return false;
    return true;
  });

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await serverUrlAuth.patch(`users/${userId}/update-status/`, {
        status: newStatus,
      });

      const updated = users.map((u) =>
        u.id === userId ? { ...u, status: newStatus } : u,
      );
      setUsers(updated);
      toast.success(`User status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleTierChange = async () => {
    if (!selectedArtisan) return;

    setIsSubmitting(true);
    try {
      // Use the correct endpoint with the user ID
      await serverUrlAuth.patch(
        `users/admin/update-tier/${selectedArtisan.id}/`,
        {
          tier: tierFormData.tier,
        },
      );

      const updated = users.map((u) =>
        u.id === selectedArtisan.id ? { ...u, tier: tierFormData.tier } : u,
      );
      setUsers(updated);
      updateTierStats(updated);

      toast.success(
        `Tier updated to ${tierFormData.tier.toUpperCase()} for ${selectedArtisan.name}`,
      );
      handleCloseTierModal();
    } catch (error) {
      console.error('Error updating tier:', error);
      toast.error('Failed to update artisan tier');
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateTierStats = (usersList) => {
    const bronze = usersList.filter(
      (u) => u.role === 'artisan' && u.tier === 'bronze',
    ).length;
    const silver = usersList.filter(
      (u) => u.role === 'artisan' && u.tier === 'silver',
    ).length;
    const gold = usersList.filter(
      (u) => u.role === 'artisan' && u.tier === 'gold',
    ).length;
   

    setStats((prev) => ({
      ...prev,
      bronze_count: bronze,
      silver_count: silver,
      gold_count: gold,
    
    }));
  };

  const openTierModal = (artisan) => {
    setSelectedArtisan(artisan);
    setTierFormData({ tier: artisan.tier || 'bronze' });
    setShowEditTierModal(true);
  };

  const handleCloseTierModal = () => {
    setShowEditTierModal(false);
    setSelectedArtisan(null);
    setTierFormData({ tier: 'bronze' });
  };

  const getRoleCount = (role) => users.filter((u) => u.role === role).length;

  const normalizePhoneNumber = (number) => {
    if (!number) return '';
    let cleaned = number.replace(/\D/g, '');
    if (cleaned.startsWith('07')) cleaned = '+254' + cleaned.substring(1);
    else if (cleaned.startsWith('254')) cleaned = '+' + cleaned;
    else if (!cleaned.startsWith('+254')) cleaned = '+254' + cleaned;
    return cleaned;
  };

  const validatePhoneNumber = (number) => {
    const cleaned = normalizePhoneNumber(number);
    return /^\+254\d{9}$/.test(cleaned);
  };

  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.full_name.trim()) {
      errors.full_name = 'Full name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.phone_number) {
      errors.phone_number = 'Phone number is required';
    } else if (!validatePhoneNumber(formData.phone_number)) {
      errors.phone_number =
        'Please enter a valid Kenyan phone number (e.g., 0700000000 or +254700000000)';
    }

    if (!formData.tier) {
      errors.tier = 'Please select a tier';
    }

    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleAddArtisan = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setFormErrors({});

    try {
      const normalizedPhone = normalizePhoneNumber(formData.phone_number);
      const payload = {
        full_name: formData.full_name,
        email: formData.email,
        phone_number: normalizedPhone,
        tier: formData.tier, // Include tier in the payload
      };

      const response = await serverUrlAuth.post(
        'users/admin/create-artisan/',
        payload,
      );

      const newArtisan = {
        ...response.data,
        tier: formData.tier,
        status: 'pending',
      };
      setUsers((prev) => [...prev, newArtisan]);

      setStats((prev) => ({
        ...prev,
        artisan_count: prev.artisan_count + 1,
        [`${formData.tier}_count`]: prev[`${formData.tier}_count`] + 1,
        total_users: prev.total_users + 1,
      }));

      toast.success(
        `Artisan has been created successfully with ${formData.tier.toUpperCase()} tier! Activation email has been sent.`,
      );
      handleCloseModal();
    } catch (error) {
      console.error('Error adding artisan:', error);
      if (error.response) {
        if (error.response.status === 409) {
          toast.error('An artisan with this email already exists.');
        } else if (error.response.data) {
          const backendErrors = error.response.data;
          if (typeof backendErrors === 'object') {
            const errorMessages = Object.values(backendErrors)
              .flat()
              .join(', ');
            toast.error(`Validation error: ${errorMessages}`);
          } else if (typeof backendErrors === 'string') {
            toast.error(backendErrors);
          }
        } else {
          toast.error('Failed to add artisan. Please try again.');
        }
      } else {
        toast.error(
          'Network error. Please check your connection and try again.',
        );
      }
      setFormErrors({
        submit: 'Failed to add artisan. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddArtisanModal(false);
    setFormData({
      full_name: '',
      email: '',
      phone_number: '',
      tier: 'bronze',
    });
    setFormErrors({});
  };

  const exportToCSV = () => {
    if (filteredUsers.length === 0) {
      toast.info('No users to export.');
      return;
    }

    const headers = [
      'ID',
      'Name',
      'Email',
      'Role',
      'Tier',
      'Status',
      'Phone',
      'Created At',
    ];
    const rows = filteredUsers.map((user) => [
      user.id,
      `"${user.name}"`,
      user.email,
      user.role,
      user.role === 'artisan' ? user.tier || 'bronze' : 'N/A',
      user.status,
      user.phone || '',
      user.created_at,
    ]);

    const csvContent = [headers, ...rows].map((e) => e.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'users_export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getTierBadgeClass = (tier) => {
    switch (tier) {
      case 'bronze':
        return 'tier-bronze';
      case 'silver':
        return 'tier-silver';
      case 'gold':
        return 'tier-gold';
      
      default:
        return 'tier-bronze';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'bronze':
        return '🥉';
      case 'silver':
        return '🥈';
      case 'gold':
        return '🥇';
     
      default:
        return '🥉';
    }
  };

  // Get tier benefits for display in form
  const getTierBenefits = (tier) => {
    switch (tier) {
      case 'bronze':
        return [
          'Basic product listing (up to 10 products)',
          'Standard support response time (48h)',
          'Basic analytics',
        ];
      case 'silver':
        return [
          'Enhanced product listing (up to 50 products)',
          'Priority support response time (24h)',
          'Advanced analytics',
          'Featured in category pages',
        ];
      case 'gold':
        return [
          'Unlimited product listing',
          'Premium support response time (12h)',
          'Real-time analytics dashboard',
          'Featured on homepage',
          'Promoted in search results',
        ];
     
      default:
        return [];
    }
  };

  if (loading) {
    return (
      <div className="admin-users">
        <div className="page-header">
          <h1>👥 User Management</h1>
          <p>Manage all users, artisans, and clients on the platform</p>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <FurnitureNav />
      <div className="admin-users">
        {/* Add Artisan Modal with Tier Selection */}
        {showAddArtisanModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2>👨‍🔧 Register New Artisan</h2>
                <p>
                  Fill in the details below to register a new artisan. They will
                  receive an activation email.
                </p>
                <button className="close-modal" onClick={handleCloseModal}>
                  ×
                </button>
              </div>

              <form onSubmit={handleAddArtisan}>
                <div className="form-group">
                  <label htmlFor="full_name">
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    placeholder="Enter artisan's full name"
                    className={formErrors.full_name ? 'error' : ''}
                    disabled={isSubmitting}
                  />
                  {formErrors.full_name && (
                    <span className="error-message">
                      {formErrors.full_name}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    className={formErrors.email ? 'error' : ''}
                    disabled={isSubmitting}
                  />
                  {formErrors.email && (
                    <span className="error-message">{formErrors.email}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="phone_number">
                    Phone Number <span className="required">*</span>
                  </label>
                  <PhoneInput
                    country="ke"
                    value={formData.phone_number}
                    onChange={(phone) =>
                      setFormData({ ...formData, phone_number: phone })
                    }
                    inputStyle={{ width: '100%', height: '45px' }}
                    buttonStyle={{ border: 'none' }}
                    containerStyle={{ width: '100%' }}
                    countryCodeEditable={false}
                    disableDropdown={true}
                    enableLongNumbers={true}
                  />
                  {formErrors.phone_number && (
                    <span className="error-message">
                      {formErrors.phone_number}
                    </span>
                  )}
                  <div className="phone-hint">
                    <small>
                      Enter a valid Kenyan phone number (e.g., 0700000000 or
                      +254700000000)
                    </small>
                  </div>
                </div>

                {/* Tier Selection Section */}
                <div className="form-group">
                  <label htmlFor="tier">
                    Artisan Tier <span className="required">*</span>
                  </label>
                  <select
                    id="tier"
                    name="tier"
                    value={formData.tier}
                    onChange={handleInputChange}
                    className={`tier-select-input ${formErrors.tier ? 'error' : ''}`}
                    disabled={isSubmitting}
                  >
                    <option value="bronze">🥉 Bronze - Basic</option>
                    <option value="silver">🥈 Silver - Enhanced</option>
                    <option value="gold">🥇 Gold - Premium</option>
                   
                  </select>
                  {formErrors.tier && (
                    <span className="error-message">{formErrors.tier}</span>
                  )}

                  {/* Dynamic Tier Benefits Display */}
                  <div className="tier-benefits-preview">
                    <h4>✨ Selected Tier Benefits:</h4>
                    <ul>
                      {getTierBenefits(formData.tier).map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="form-note">
                  <p>
                    💡 <strong>Note:</strong> The artisan will:
                  </p>
                  <ul>
                    <li>Receive an activation email with instructions</li>
                    <li>
                      Be set to "pending" status until they activate their
                      account
                    </li>
                    <li>Need to set their own password upon activation</li>
                    <li>
                      Start with the selected{' '}
                      <strong>{formData.tier.toUpperCase()}</strong> tier
                      benefits
                    </li>
                  </ul>
                </div>

                {formErrors.submit && (
                  <div className="submit-error">
                    <span className="error-message">{formErrors.submit}</span>
                  </div>
                )}

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Registering...
                      </>
                    ) : (
                      'Register Artisan'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Tier Modal */}
        {showEditTierModal && selectedArtisan && (
          <div className="modal-overlay">
            <div className="modal-content tier-modal">
              <div className="modal-header">
                <h2>🏆 Update Artisan Tier</h2>
                <p>Change tier for {selectedArtisan.name}</p>
                <button className="close-modal" onClick={handleCloseTierModal}>
                  ×
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleTierChange();
                }}
              >
                <div className="form-group">
                  <label htmlFor="tier">Select Tier</label>
                  <select
                    id="tier"
                    value={tierFormData.tier}
                    onChange={(e) => setTierFormData({ tier: e.target.value })}
                    className="tier-select"
                    disabled={isSubmitting}
                  >
                    <option value="bronze">🥉 Bronze - Basic features</option>
                    <option value="silver">
                      🥈 Silver - Enhanced visibility
                    </option>
                    <option value="gold">🥇 Gold - Priority support</option>
                   
                  </select>
                </div>

                <div className="tier-benefits">
                  <h4>Tier Benefits:</h4>
                  {tierFormData.tier === 'bronze' && (
                    <ul>
                      <li>✓ Basic product listing (up to 10 products)</li>
                      <li>✓ Standard support response time (48h)</li>
                      <li>✓ Basic analytics</li>
                    </ul>
                  )}
                  {tierFormData.tier === 'silver' && (
                    <ul>
                      <li>✓ Enhanced product listing (up to 50 products)</li>
                      <li>✓ Priority support response time (24h)</li>
                      <li>✓ Advanced analytics</li>
                      <li>✓ Featured in category pages</li>
                    </ul>
                  )}
                  {tierFormData.tier === 'gold' && (
                    <ul>
                      <li>✓ Unlimited product listing</li>
                      <li>✓ Premium support response time (12h)</li>
                      <li>✓ Real-time analytics dashboard</li>
                      <li>✓ Featured on homepage</li>
                      <li>✓ Promoted in search results</li>
                    </ul>
                  )}
                 
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseTierModal}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Updating...
                      </>
                    ) : (
                      'Update Tier'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="page-header">
          <div className="header-content">
            <div className="header-text">
              <h1>👥 User Management</h1>
              <p>Manage all users, artisans, and clients on the platform</p>
            </div>
            <div className="header-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddArtisanModal(true)}
                style={{ marginRight: '10px' }}
              >
                👨‍🔧 Add Artisan
              </button>
              <button className="btn btn-primary" onClick={exportToCSV}>
                📊 Export Report
              </button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="stats-overview">
          <div className="stat-card total-users">
            <div className="stat-content">
              <div className="stat-icon">👥</div>
              <div className="stat-info">
                <h3>Total Users</h3>
                <p className="stat-value">{users.length}</p>
              </div>
            </div>
          </div>

          <div className="stat-card artisans">
            <div className="stat-content">
              <div className="stat-icon">🔨</div>
              <div className="stat-info">
                <h3>Artisans</h3>
                <p className="stat-value">{getRoleCount('artisan')}</p>
              </div>
            </div>
          </div>

          <div className="stat-card clients">
            <div className="stat-content">
              <div className="stat-icon">💼</div>
              <div className="stat-info">
                <h3>Clients</h3>
                <p className="stat-value">{getRoleCount('client')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tier Distribution Stats */}
        {getRoleCount('artisan') > 0 && (
          <div className="tier-stats-overview">
            <h3>🏆 Artisan Tier Distribution</h3>
            <div className="tier-stats-container">
              <div className="tier-stat-card bronze">
                <div className="tier-icon">🥉</div>
                <div className="tier-info">
                  <h4>Bronze</h4>
                  <p className="tier-count">{stats.bronze_count}</p>
                  <span className="tier-percentage">
                    {(
                      (stats.bronze_count / getRoleCount('artisan')) *
                      100
                    ).toFixed(1)}
                    %
                  </span>
                </div>
              </div>
              <div className="tier-stat-card silver">
                <div className="tier-icon">🥈</div>
                <div className="tier-info">
                  <h4>Silver</h4>
                  <p className="tier-count">{stats.silver_count}</p>
                  <span className="tier-percentage">
                    {(
                      (stats.silver_count / getRoleCount('artisan')) *
                      100
                    ).toFixed(1)}
                    %
                  </span>
                </div>
              </div>
              <div className="tier-stat-card gold">
                <div className="tier-icon">🥇</div>
                <div className="tier-info">
                  <h4>Gold</h4>
                  <p className="tier-count">{stats.gold_count}</p>
                  <span className="tier-percentage">
                    {(
                      (stats.gold_count / getRoleCount('artisan')) *
                      100
                    ).toFixed(1)}
                    %
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-bar">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
              />
              <span className="search-icon">🔍</span>
            </div>

            <div className="filter-group">
              <select
                value={filters.role}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, role: e.target.value }))
                }
              >
                <option value="all">All Roles</option>
                <option value="client">Clients</option>
                <option value="artisan">Artisans</option>
                <option value="admin">Admins</option>
              </select>

              <select
                value={filters.tier}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, tier: e.target.value }))
                }
                disabled={filters.role !== 'artisan' && filters.role !== 'all'}
              >
                <option value="all">All Tiers</option>
                <option value="bronze">🥉 Bronze</option>
                <option value="silver">🥈 Silver</option>
                <option value="gold">🥇 Gold</option>
                
              </select>

              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div className="filter-actions">
              <button
                className="btn btn-secondary"
                onClick={() =>
                  setFilters({
                    role: 'all',
                    status: 'all',
                    tier: 'all',
                    search: '',
                  })
                }
              >
                Clear Filters
              </button>
              <span className="results-count">
                {filteredUsers.length} of {users.length} users
              </span>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="table-section">
          <div className="table-header">
            <h3>User Accounts</h3>
          </div>

          <div className="table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" className="select-all" />
                  </th>
                  <th>User</th>
                  <th>Role</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className={user.status}>
                    <td>
                      <input type="checkbox" className="user-select" />
                    </td>
                    <td>
                      <div className="user-info">
                        <div className="user-avatar">
                          {user.name?.charAt(0) || 'U'}
                        </div>
                        <div className="user-details">
                          <strong>{user.name}</strong>
                          <span>{user.email}</span>
                          {user.phone && (
                            <span className="user-phone">{user.phone}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`role-badge ${user.role}`}>
                        <span className="role-icon">
                          {user.role === 'artisan'
                            ? '🔨'
                            : user.role === 'admin'
                              ? '⚙️'
                              : '💼'}
                        </span>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      {user.role === 'artisan' ? (
                        <span
                          className={`tier-badge ${getTierBadgeClass(user.tier)}`}
                        >
                          {getTierIcon(user.tier)}{' '}
                          {user.tier?.toUpperCase() || 'BRONZE'}
                        </span>
                      ) : (
                        <span className="tier-badge na">—</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${user.status}`}>
                        <span className="status-dot"></span>
                        {user.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Link
                          to={`/admin/users/${user.id}`}
                          className="btn btn-sm btn-outline view-btn"
                          title="View Details"
                        >
                          👁️ View
                        </Link>
                        {user.role === 'artisan' && (
                          <button
                            className="btn btn-sm btn-tier"
                            onClick={() => openTierModal(user)}
                            title="Update Tier"
                          >
                            🏆 Tier
                          </button>
                        )}
                        <div className="dropdown-actions">
                          <select
                            value={user.status}
                            onChange={(e) =>
                              handleStatusChange(user.id, e.target.value)
                            }
                            className="status-select"
                          >
                            <option value="active">Active</option>
                            <option value="pending">Pending</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredUsers.length === 0 && (
              <div className="empty-state">
                <div className="empty-icon">👥</div>
                <h3>No users found</h3>
                <p>
                  No users match your current filter criteria. Try adjusting
                  your search or filters.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() =>
                    setFilters({
                      role: 'all',
                      status: 'all',
                      tier: 'all',
                      search: '',
                    })
                  }
                >
                  Clear All Filters
                </button>
              </div>
            )}
          </div>

          <div className="table-footer">
            <div className="footer-info">
              Showing {filteredUsers.length} of {users.length} users
            </div>
            <div className="pagination">
              <button className="page-btn" disabled>
                ← Previous
              </button>
              <span className="page-info">Page 1 of 1</span>
              <button className="page-btn" disabled>
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withAuth(UsersManagement, 'admin');
